import React from "react";
import { useTheme } from "../../../../../../context/themeContext";
import { useTranslate } from "../../../../../../context/languageContext";
import { R } from "../../../../../../R";
import RouteSynoptic from "./RouteSynoptic";
import ReactTooltip from "react-tooltip";
import { MdStairs as Escalator, MdElevator as Elevator } from "react-icons/md";
import { VM } from "../../../../../../../viewmodel/VM";

export default function RouteCard(props, ref) {
    const theme = useTheme();
    const t = useTranslate();

    const WheelChair = R.drawables.general.Ic_silladeruedas;
    const Euro = R.drawables.general.Ic_Precio;
    const ExclamationIcon = R.drawables.commons.exclaimation;

    const normalizeToArray = (value) => {
        if (Array.isArray(value)) return value;

        if (Array.isArray(value?.data)) return value.data;
        if (Array.isArray(value?.lines)) return value.lines;
        if (Array.isArray(value?.items)) return value.items;

        if (value && typeof value === "object") {
            return Object.values(value);
        }

        return [];
    };

    const getLineCode = (line) => {
        return String(
            line?.code ??
            line?.tripShortName ??
            line?.shortName ??
            line?.routeShortName ??
            line?.codigo ??
            line?.codigoLinea ??
            ""
        );
    };

    const getLineId = (line) => {
        return (
            line?.idLinea ??
            line?.id ??
            line?.lineId ??
            line?.routeId ??
            line?.idRoute ??
            null
        );
    };

    const getTransportLegs = () => {
        return normalizeToArray(props.info?.lineSynoptic).filter(
            (leg) => leg?.mode !== "WALK"
        );
    };

    const getMatchedPlanLineIds = () => {
        const allLines = normalizeToArray(VM.lines?.data);
        const transportLegs = getTransportLegs();

        return allLines
            .map((line) => {
                const matchedLeg = transportLegs.find((leg) => {
                    return getLineCode(line) === String(leg?.tripShortName ?? "");
                });

                if (!matchedLeg) return null;

                return getLineId(line);
            })
            .filter((id) => id != null)
            .map((id) => String(id));
    };

    const hasLineAlerts = () => {
        const alerts = normalizeToArray(VM.alerts?.data);
        const planLineIds = getMatchedPlanLineIds();

        if (!planLineIds.length) return false;

        return alerts.some((alert) => {
            const alertLines = alert?.lineas ?? [];

            return alertLines.some((alertLine) =>
                planLineIds.includes(String(alertLine?.idLinea))
            );
        });
    };

    const isRealStop = (stop) => {
        return !!(stop?.stopId || stop?.stopCode);
    };

    const getPlanStops = () => {
        const lineSynoptic = normalizeToArray(props.info?.lineSynoptic);
        const added = new Set();

        return lineSynoptic
            .flatMap((leg) => [
                leg?.from,
                ...(leg?.intermediateStops ?? []),
                leg?.to
            ])
            .filter((stop) => stop && isRealStop(stop))
            .filter((stop) => {
                const key = String(stop?.stopId ?? stop?.stopCode);

                if (added.has(key)) return false;

                added.add(key);
                return true;
            });
    };

    const getStopIdentifiers = (stop) => {
        return [
            stop?.stopId,
            stop?.stopCode,
            stop?.idParada,
            stop?.codigoParada
        ]
            .filter(Boolean)
            .map((value) => String(value));
    };

    const getAlertStopIdentifiers = (alert) => {
        const paradas = alert?.paradas ?? [];

        const idsFromParadas = paradas.flatMap((stop) => [
            stop?.stopId,
            stop?.stopCode,
            stop?.idParada,
            stop?.codigoParada,
            stop?.codParada,
            stop?.code,
            stop?.id
        ]);

        return [
            alert?.alertStopId,
            ...idsFromParadas
        ]
            .filter(Boolean)
            .map((value) => String(value));
    };

    const hasStopAlerts = () => {
        const alerts = normalizeToArray(VM.alerts?.data);
        const planStops = getPlanStops();

        if (!planStops.length) return false;

        return alerts.some((alert) => {
            const alertStopIds = getAlertStopIdentifiers(alert);

            return planStops.some((stop) => {
                const stopIds = getStopIdentifiers(stop);

                return stopIds.some((stopId) =>
                    alertStopIds.includes(stopId)
                );
            });
        });
    };

    const hasAnyAlerts = () => {
        return hasLineAlerts() || hasStopAlerts();
    };

    const getMinutes = (time) => {
        if (typeof time === "number") {
            const date = new Date(time);
            return date.getHours() * 60 + date.getMinutes();
        }

        if (typeof time === "string") {
            const [hours, minutes] = time.split(":");

            const h = Number(hours);
            const m = Number(minutes);

            if (Number.isNaN(h) || Number.isNaN(m)) return null;

            return h * 60 + m;
        }

        return null;
    };

    const hasDelayOverOneMinute = (time, estimatedTime) => {
        const timeMin = getMinutes(time);
        const estimatedMin = getMinutes(estimatedTime);

        console.log("COMPARANDO", {
            time,
            estimatedTime,
            timeMin,
            estimatedMin,
            diff:
                timeMin != null && estimatedMin != null
                    ? Math.abs(timeMin - estimatedMin)
                    : null
        });

        return (
            timeMin != null &&
            estimatedMin != null &&
            Math.abs(timeMin - estimatedMin) > 1
        );
    };

    const hasAnyTimeUpdate = () => {
        const lineSynoptic = normalizeToArray(props.info?.lineSynoptic);

        return lineSynoptic.some((leg) => {
            console.log("LEG", {
                mode: leg?.mode,
                startTime: leg?.startTime,
                endTime: leg?.endTime,
                estimatedFrom: leg?.from?.estimatedArrival,
                estimatedTo: leg?.to?.estimatedArrival,
                tripShortName: leg?.tripShortName
            });

            if (leg?.mode === "WALK") {
                return false;
            }

            return (
                hasDelayOverOneMinute(
                    leg?.startTime,
                    leg?.from?.estimatedArrival
                ) ||
                hasDelayOverOneMinute(
                    leg?.endTime,
                    leg?.to?.estimatedArrival
                )
            );
        });
    };

    const styles = {
        content: {
            background: theme.colors.white,
            borderRadius: "12px",
            padding: 12,
            textAlign: "left",
            cursor: "pointer",
        },
        row: {
            display: "flex",
        },
        row_euro: {
            display: "flex",
            alignItems: "center"
        },
        column: {
            display: "flex",
            width: "100%",
            flexDirection: "column"
        },
        selector: {
            borderColor: "white",
            border: props.selected
                ? `2px solid ${theme.colors.primaryMedium}`
                : `0px solid ${theme.colors.white}`,
            backgroundColor: props.selected
                ? theme.colors.primaryMedium
                : theme.colors.white,
            width: 0,
            marginRight: props.selected ? 15 : 0,
            transition: "all 300ms linear",
        },
        separator: {
            backgroundColor: theme.colors.border,
            height: 1,
            marginBottom: 5,
            width: "100%"
        },
        time: {
            fontSize: 12,
            fontWeight: 600,
            marginLeft: "auto",
            lineHeight: "240%",
        },
        duration: {
            fontSize: 18,
            fontWeight: 700,
            marginRight: 2
        },
        duration2: {
            fontSize: 15,
            lineHeight: "180%",
            fontWeight: 600,
            marginRight: 3
        },
        icon: {
            fill: theme.colors.darkGray,
            width: 18,
            marginRight: 5,
            marginTop: 5
        },
        alertIcon: {
            width: 16,
            height: 16,
            marginLeft: "auto"
        },
        mainText: {
            marginBottom: 10,
            fontSize: 14,
            fontWeight: 600,
        },
        subText: {
            fontSize: 14,
            fontWeight: 600,
            lineHeight: "240%",
        },   
        timeUpdateText: {
            fontSize: 12,
            color: theme.colors.darkGray,
            marginTop: 6,
            lineHeight: "150%"
        }
    };

    return (
        <div style={props.style} onClick={props.onClick}>
            <div style={styles.content}>
                <div style={styles.row}>
                    <div style={styles.selector} />
                    <div style={styles.column}>

                        {props.title
                            ? <div style={styles.mainText}>{props.title}</div>
                            : null
                        }

                        <div style={styles.row}>
                            {props.info.duration / 60 < 1
                                ? null
                                : <div style={styles.duration}>
                                    {Math.floor(props.info.duration / 60)}
                                </div>
                            }

                            {props.info.duration / 60 < 1
                                ? null
                                : <div style={styles.duration2}>
                                    {t("h")}
                                </div>
                            }

                            <div style={styles.duration}>{props.info.duration % 60}</div>
                            <div style={styles.duration2}>{t("min")}</div>
                            <div style={styles.time}>{props.info.startTime + "-" + props.info.endTime}</div>
                        </div>

                        <RouteSynoptic lineSynoptic={props.info.lineSynoptic} />

                        <div style={styles.separator} />

                        <div style={{ ...styles.row_euro, width: "100%" }}>
                            <ReactTooltip effect={"solid"} id="main" />

                            <div
                                style={{ ...styles.row_euro, marginRight: 20 }}
                                data-tip={t("simpleTicket")}
                                data-for={"main"}
                            >
                                <Euro style={styles.icon} />
                                <div style={styles.subText}>{props.info.price}</div>
                            </div>

                            <div className="ascensores_y_escaleras">
                                {props.info.hasSteps ?
                                    <>
                                        <ReactTooltip effect={"solid"} id="escalera" />
                                        <div data-tip={"La ruta contiene escaleras"} data-for={"escalera"}>
                                            <Escalator className={"darkGray"} style={{ fontSize: 24 }} />
                                        </div>
                                    </>
                                    : null
                                }

                                {props.info.hasElevator ?
                                    <>
                                        <ReactTooltip effect={"solid"} id="ascensor" />
                                        <div data-tip={"La ruta contiene ascensores"} data-for={"ascensor"}>
                                            <Elevator className={"darkGray"} style={{ fontSize: 24 }} />
                                        </div>
                                    </>
                                    : null
                                }
                            </div>

                            {props.info.accessible
                                ? <div style={styles.row}>
                                    <WheelChair style={styles.icon} />
                                    <div style={styles.subText}>{t("accessible")}</div>
                                </div>
                                : null
                            }

                            {hasAnyAlerts() ? (
                                <ExclamationIcon
                                    style={styles.alertIcon}
                                    data-tip={"La ruta contiene incidencias"}
                                    data-for={"main"}
                                />
                            ) : null}
                        </div>
                        
                        {hasAnyTimeUpdate() ? (
                            <div style={styles.timeUpdateText}>
                                Esta planificación contiene adelantos o retrasos sobre el horario programado.
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}